import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { Cacheable } from "ts-cacheable";

import { environment } from "@environments/environment";

import { Pais } from "@shared/Entities/Maritima/pais.entity";

import { IAcselBiciBrand } from "@Unit/shared/Type/Interfaces/AcselInterface/acsel-bici-brand.interface";
import { IAcselBici } from "@Unit/shared/Type/Interfaces/AcselInterface/acsel-bici.interface";
import { IAcselBusinessRule } from "@Unit/shared/Type/Interfaces/AcselInterface/acsel-business-rules.interface";
import { IAcselCoupon } from "@Unit/shared/Type/Interfaces/AcselInterface/acsel-coupon.interface";
import {
  IAcselElectronicEquipment,
  IAcselEquipmentType,
} from "@Unit/shared/Type/Interfaces/AcselInterface/acsel-electronic-equipment";
import { IAcselEmergenciaHogar } from "@Unit/shared/Type/Interfaces/AcselInterface/acsel-emergencia-hogar.interface";
import { IAcselMejorAmigo } from "@Unit/shared/Type/Interfaces/AcselInterface/acsel-mejor-amigo.interface";
import { IAcselPetBreef } from "@Unit/shared/Type/Interfaces/AcselInterface/acsel-pets.interface";
import { IAcselQuoteType } from "@Unit/shared/Type/Interfaces/AcselInterface/acsel-quote-type.interface";
import { IAcselRespuestaEmision } from "@Unit/shared/Type/Interfaces/AcselInterface/acsel-resputa-emision.interface";
import { IAcselRespuestaEmpresaRnc } from "@Unit/shared/Type/Interfaces/AcselInterface/acsel-resputa-empresa-rnc.interface";
import { IAcselVehiculoDato } from "@Unit/shared/Type/Interfaces/AcselInterface/acsel-vehiculo-dato.interface";
import { IAcselVidaCredito } from "@Unit/shared/Type/Interfaces/AcselInterface/acsel-vida-info.interface";
import { IAcselSeguroDeAuto } from "@Unit/shared/Type/Interfaces/AcselInterface/acselSeguroDeAuto.interface";
import { IBusinessRule } from "@Unit/shared/Type/Interfaces/business-rules.interface";
import { IClientBlackList, IClientPadron } from "@Unit/shared/Type/Interfaces/client-padronl.interface";
import { IFileUploadResponse, ITuitionResponse } from "@Unit/shared/Type/Interfaces/file-upload-response.interface";
import { IIntermediary } from "@Unit/shared/Type/Interfaces/intermediary.interface";
import { IPaymentFormToken } from "@Unit/shared/Type/Interfaces/payment-form-token.interface";
import { IQuoteType } from "@Unit/shared/Type/Interfaces/quote-type.interface";
import { IVehicleBrand } from "@Unit/shared/Type/Interfaces/vehicle-brand.interface";
import { IVehicleChasisRestriction } from "@Unit/shared/Type/Interfaces/vehicle-chasis-restriction.interface";
import { IVehicleData } from "@Unit/shared/Type/Interfaces/vehicle-data.interface";
import { IVehicleModel } from "@Unit/shared/Type/Interfaces/vehicle-model.interface";

import { IFinancialInstitution } from "../shared/Type/Interfaces/institutionFinantial.interface";

type acselInterfaces =
  | IAcselSeguroDeAuto
  | IAcselVidaCredito
  | IAcselElectronicEquipment
  | IAcselBici
  | IAcselMejorAmigo
  | IAcselEmergenciaHogar;

@Injectable({
  providedIn: "root",
})
export class AcselService {
  constructor(private http: HttpClient) {}

  //Private Variables
  private readonly baseUrl = environment.enlineaBackendUrl;
  private readonly iPlusUrl = environment.unit.iPlusUrl;
  private readonly blackListUrl = environment.unit.universalBlackList;

  //Public Variables

  //Private Functions

  private createElementByForm(
    tagName: string,
    idElement: string,
    nameElement: string,
    urlElement: string,
    methodElement: string
  ): HTMLFormElement {
    const element = document.createElement(tagName) as HTMLFormElement;
    element.method = methodElement;
    element.action = urlElement;
    element.name = nameElement;
    element.id = idElement;
    return element;
  }

  private createElementByTextarea(
    tagName: string,
    idElement: string,
    nameElement: string,
    innerText: string
  ): HTMLTextAreaElement {
    const element = document.createElement(tagName) as HTMLTextAreaElement;
    element.name = nameElement;
    element.id = idElement;
    element.innerText = innerText;
    element.setAttribute("hidden", "hidden");
    return element;
  }

  private mapQuoteTypeFromServer(quoteType: IAcselQuoteType): IQuoteType {
    return {
      codClient: quoteType.codCli,
      idePol: quoteType.idePol,
      codType: quoteType.codTipo,
      issueType: quoteType.tipoEmision,
      issueDescription: quoteType.descripcionEmision,
      issueName: quoteType.nombreEmision,
      intermediaryCode: quoteType.codigoIntermediario,
      codProd: quoteType.codProd,
      priceInText: quoteType.mensajeEmision,
    };
  }

  private mapBusinessRuleFromServer(businessRule: IAcselBusinessRule): IBusinessRule {
    return {
      codRule: businessRule.codRegla,
      value: businessRule.valorComparacion,
      codType: businessRule.codTipo,
      rule: {
        codRule: businessRule.regla.codRegla,
        dataType: businessRule.regla.tipoDato,
        description: businessRule.regla.descripcion,
      },
    };
  }

  private mapVehicleFromServer(vehicleData: IAcselVehiculoDato): IVehicleData {
    return {
      idModel: vehicleData.idModelo,
      idBrand: vehicleData.idMarca,
      idType: vehicleData.idTipo,
      name: vehicleData.nombre,
      action: vehicleData.accion,
      excluded: vehicleData.excluido,
      discount: vehicleData.descuento,
      inactive: vehicleData.estaInactivo,
      brand: {
        idBrand: vehicleData.marca.idMarca,
        name: vehicleData.marca.nombre,
        discount: vehicleData.marca.descuento,
        inactive: vehicleData.marca.estaInactivo,
      },
      type: {
        idType: vehicleData.tipo.idTipo,
        name: vehicleData.tipo.nombre,
        inactive: vehicleData.tipo.estaInactivo,
      },
    };
  }

  //Public Functions
  @Cacheable()
  public getBicycles(): Observable<Array<IAcselBiciBrand>> {
    return this.http.get<Array<IAcselBiciBrand>>(`${this.baseUrl}/api/unit/bikes`).pipe(
      map((response: object) => {
        return response["data"];
      })
    );
  }

  @Cacheable()
  public getBicycle(idBicycle: number): Observable<IAcselBiciBrand> {
    return this.http.get<IAcselBiciBrand>(`${this.baseUrl}/api/unit/bikes/${idBicycle}`);
  }

  @Cacheable()
  public getPets(): Observable<Array<IAcselPetBreef>> {
    return this.http.get<Array<IAcselPetBreef>>(`${this.baseUrl}/api/unit/pets`).pipe(
      map((response: object) => {
        return response["data"];
      })
    );
  }

  @Cacheable()
  public getPet(idPet: number): Observable<IAcselPetBreef> {
    return this.http.get<IAcselPetBreef>(`${this.baseUrl}/api/unit/pets/${idPet}`).pipe(
      map((response: object) => {
        return response["data"];
      })
    );
  }

  public getClientById(id: string): Observable<IClientPadron> {
    return this.http.get<IClientPadron>(`${this.baseUrl}/v2/${id}`);
  }

  public getClientBlackList(id: string): Observable<IClientBlackList> {
    return this.http.get<IClientBlackList>(`${this.blackListUrl}/RestrictedEntries/existAnyWith/onBody?Document=${id}`);
  }

  public getCompanyByRnc(rnc: string): Observable<IAcselRespuestaEmpresaRnc> {
    return this.http.get<IAcselRespuestaEmpresaRnc>(`${this.baseUrl}/api/unit/company/${rnc}`);
  }

  @Cacheable()
  public getCountries(): Observable<Array<Pais>> {
    return this.http.get<Array<Pais>>(`${this.baseUrl}/api/unit/direcciones/paises`);
  }

  public getCoupon(coupon: string): Observable<IAcselCoupon> {
    return this.http.get<IAcselCoupon>(`${this.baseUrl}/api/unit/coupons/${coupon}`);
  }

  public getDocumentLink(documentType: string, policy: string): Observable<Blob> {
    return this.http.get<Blob>(
      `${this.baseUrl}/api/unit/documents?referencia=${policy}&tipoOperacion=Emision&tipoAdjunto=${documentType}`,
      { responseType: "blob" as "json" }
    );
  }

  @Cacheable()
  public getElectronicEquipments(): Observable<IAcselEquipmentType[]> {
    return this.http.get<IAcselEquipmentType[]>(`${this.baseUrl}/api/unit/equipments`);
  }

  @Cacheable()
  public getFinancialInstitutions(): Observable<IFinancialInstitution[]> {
    return this.http.get<IFinancialInstitution[]>(`${this.baseUrl}/api/unit/instituciones-financieras`);
  }

  @Cacheable()
  public getIntermediaries(codInter?: string): Observable<Array<IIntermediary>> {
    if (codInter) return this.http.get<Array<IIntermediary>>(`${this.baseUrl}/api/unit/intermediarios?codInter=${codInter}`);
    else return this.http.get<Array<IIntermediary>>(`${this.baseUrl}/api/unit/intermediarios`);
  }

  @Cacheable()
  public getMunicipality(province: string): Observable<Array<string>> {
    return this.http.get<Array<string>>(`${this.baseUrl}/api/unit/direcciones/municipios?provincia=${province}`);
  }

  @Cacheable()
  public getProvinces(): Observable<Array<string>> {
    return this.http.get<Array<string>>(`${this.baseUrl}/api/unit/direcciones/provincias`);
  }

  public getQuotePolicy(quoteId: string): Observable<acselInterfaces> {
    return this.http.get<acselInterfaces>(`${this.baseUrl}/api/unit/policies/quotes/${quoteId}`);
  }

  public getQuoteTypes(codProd: string): Observable<Array<IQuoteType>> {
    return this.http.get<Array<IAcselQuoteType>>(`${this.baseUrl}/api/unit/Policies/tipos?codProd=${codProd}`).pipe(
      map((response: Array<IAcselQuoteType>) => {
        return response.map((quoteType) => this.mapQuoteTypeFromServer(quoteType));
      })
    );
  }

  public getQuoteBusinessRule(codType: string): Observable<Array<IBusinessRule>> {
    return this.http.get<Array<IAcselBusinessRule>>(`${this.baseUrl}/api/unit/quotes/reglas?codTipo=${codType}`).pipe(
      map((response: Array<IAcselBusinessRule>) => {
        return response.map((businessRules) => this.mapBusinessRuleFromServer(businessRules));
      })
    );
  }

  @Cacheable()
  public getSectors(province: string, municipality: string): Observable<Array<string>> {
    return this.http.get<Array<string>>(
      `${this.baseUrl}/api/unit/direcciones/sectores?provincia=${province}&municipio=${municipality}`
    );
  }

  public getPolicy(policyId: string): Observable<acselInterfaces> {
    return this.http.get<acselInterfaces>(`${this.baseUrl}/api/unit/policies/${policyId}`);
  }

  public getQuote(quoteId: string): Observable<acselInterfaces> {
    return this.http.get<acselInterfaces>(`${this.baseUrl}/api/unit/quotes/${quoteId}`);
  }

  @Cacheable()
  public getVehicleBrands(): Observable<Array<IVehicleBrand>> {
    return this.http.get<Array<IVehicleBrand>>(`${this.baseUrl}/api/unit/vehicles/marcas`).pipe(
      map((response: object) => {
        return response["data"];
      })
    );
  }

  @Cacheable()
  public getVehicleData(modelId: number): Observable<IVehicleData> {
    return this.http.get<IAcselVehiculoDato>(`${this.baseUrl}/api/unit/Vehicles/${modelId}`).pipe(
      map((response: IAcselVehiculoDato) => {
        return this.mapVehicleFromServer(response);
      })
    );
  }

  @Cacheable()
  public getVehicleModels(brandId: number): Observable<Array<IVehicleModel>> {
    return this.http.get<Array<IVehicleBrand>>(`${this.baseUrl}/api/unit/vehicles/modelos?marca=${brandId}`).pipe(
      map((response: object) => {
        return response["data"];
      })
    );
  }

  public getPaymentForm(ideProp: number, paymentType?: number): Observable<IPaymentFormToken> {
    //Payment Type debe ser:  0 Hold, 1 Post, 2 Void, 3 Sale
    paymentType = paymentType === 0 ? paymentType : 3;
    return this.http.post<IPaymentFormToken>(`${this.baseUrl}/api/unit/payment/payment-form/${ideProp}`, {
      urlAprobado: `${window.location.href}/pago-aprobado`,
      urlCancelado: `${window.location.href}`,
      urlDeclinado: `${window.location.href}/pago-declinado`,
      tipoPago: paymentType,
    });
  }

  public issuePolicy(quoteId: number): Observable<IAcselRespuestaEmision> {
    return this.http.post<IAcselRespuestaEmision>(`${this.baseUrl}/api/unit/Quotes/emitir/${quoteId}`, {});
  }

  public issueCertificate(quoteId: number, parentPolicy: number): Observable<IAcselRespuestaEmision> {
    return this.http.post<IAcselRespuestaEmision>(`${this.baseUrl}/api/unit/Quotes/emitir-certificado`, {
      numeroCotizacion: quoteId,
      idePol: parentPolicy,
    });
  }

  public processQuoteData(quoteData: acselInterfaces): Observable<acselInterfaces> {
    return of(quoteData);
  }

  public postpaymentGateways(payloadHash: string): void {
    const iplusPayForm = this.createElementByForm("form", "fmDatos", "fmDatos", this.iPlusUrl, "POST");
    const iPlusPayTextarea = this.createElementByTextarea("textarea", "data", "data", payloadHash);
    iplusPayForm.appendChild(iPlusPayTextarea);
    document.body.appendChild(iplusPayForm);
    iplusPayForm.submit();
  }

  public requestInspectionVideo(quoteId: number, parentPolicy: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/api/unit/communication`, {
      identificacion: parentPolicy ? `${quoteId}-${parentPolicy}` : `${quoteId}`,
      tipo: "InspeccionAliado",
    });
  }

  public requestQuoteApprove(quoteId: number, parentPolicy: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/api/unit/communication`, {
      identificacion: parentPolicy ? `${quoteId}-${parentPolicy}` : `${quoteId}`,
      tipo: "Aprobacion",
    });
  }

  public requestQuotePayment(quoteId: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/api/unit/communication`, {
      identificacion: quoteId,
      tipo: "Pago",
    });
  }

  public setQuote(quoteData: acselInterfaces, quoteId?: number): Observable<acselInterfaces> {
    console.warn(quoteData.data["vehiculo"]);
    if (quoteId) {
      return this.http.put<acselInterfaces>(`${this.baseUrl}/api/unit/Quotes/${quoteId}`, quoteData);
    } else {
      return this.http.post<acselInterfaces>(`${this.baseUrl}/api/unit/Quotes`, quoteData);
    }
  }

  public uploadDocument(
    quoteId: number,
    formData: FormData,
    transactionType: string,
    operationType: string
  ): Observable<IFileUploadResponse | ITuitionResponse> {
    // transactionType - Ninguno | Matricula | Inspeccion | Reclamacion | Imagen | VideoInspeccion | Pasaporte
    // operationType - Cotizacion | Emision | Renovacion | Endoso | Reclamacion
    return this.http.post<IFileUploadResponse | ITuitionResponse>(
      `${this.baseUrl}/api/unit/Documents?tipoTransaccion=${transactionType}&tipoOperacion=${operationType}&referencia=${quoteId}`,
      formData
    );
  }

  public uploadTuition(quoteId: number, formData: FormData): Observable<ITuitionResponse> {
    return this.http.post<ITuitionResponse>(`${this.baseUrl}/api/unit/Documents/matricula?referencia=${quoteId}`, formData);
  }

  public sendInvoiceToClient(policyNumber: string, invoiceId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/api/unit/communication`, {
      identificacion: `${policyNumber}|${invoiceId}`,
      tipo: "ConfirmacionPago",
    });
  }

  public validateChasis(chasis: string): Observable<IVehicleChasisRestriction> {
    return this.http.get<IVehicleChasisRestriction>(`${this.baseUrl}/api/unit/Vehicles/restriction/${chasis}`);
  }
}
